import {
  ChannelRuleDto,
  EngagementDto,
  FitTier,
  IcpVersionDto,
  LeadAccountDetailDto,
  LeadAccountDto,
  LeadEventDto,
  LeadPersonDetailDto,
  LeadPersonDto,
  LeadsOverviewDto,
  LeadStage,
  OutreachMessageDto,
  SenderDto,
  SendingSettingsDto,
  ToWriteDto,
  UpdateMessageDto,
} from "@growth/dto";

import { type SampleAccount, type SampleDb, type SamplePerson, seedFor } from "./leads-sample-data";

/**
 * A stand-in for the leads API while the screens are drafted. It keeps one sample per brand in
 * the browser and applies the same rules the server will: fit by check weights, scores from
 * human events only, contact routes from the Australian rules, and checks before sending.
 */

const KEY = "ge.leads.sample.v2.";
const cache = new Map<string, SampleDb>();

const load = (brandId: string, brandName: string): SampleDb => {
  const hit = cache.get(brandId);
  if (hit) return hit;
  let db: SampleDb | null = null;
  try {
    const raw = window.localStorage.getItem(KEY + brandId);
    if (raw) db = JSON.parse(raw) as SampleDb;
  } catch {
    db = null;
  }
  db ??= structuredClone(seedFor(brandName));
  cache.set(brandId, db);
  return db;
};

const save = (brandId: string, db: SampleDb) => {
  cache.set(brandId, db);
  try {
    window.localStorage.setItem(KEY + brandId, JSON.stringify(db));
  } catch {
    // Private windows and full storage: the sample still works until reload.
  }
};

export const resetSample = (brandId: string) => {
  cache.delete(brandId);
  try {
    window.localStorage.removeItem(KEY + brandId);
  } catch {
    // nothing to clear
  }
};

const now = () => new Date().toISOString();
const days = (iso: string) => (Date.now() - new Date(iso).getTime()) / 86_400_000;
const firstName = (name: string) => name.split(/\s+/)[0] ?? name;
let seq = 0;
const newId = (prefix: string) => `${prefix}-${Date.now().toString(36)}${(++seq).toString(36)}`;

/** Pretend network delay, so loading states show. */
const later = <T>(value: () => T, ms = 220) =>
  new Promise<T>((resolve, reject) =>
    setTimeout(() => {
      try {
        resolve(value());
      } catch (error) {
        reject(error);
      }
    }, ms),
  );

// ─── Rules ───────────────────────────────────────────────────────────────────────────────────

export const tierFor = (percent: number, tiers: IcpVersionDto["tiers"]): FitTier =>
  percent >= tiers.A ? "A" : percent >= tiers.B ? "B" : percent >= tiers.C ? "C" : "D";

const fitOf = (db: SampleDb, account: SampleAccount) => {
  const max = account.checks.reduce((sum, check) => sum + check.weight, 0);
  const score = account.checks
    .filter((check) => check.verdict === "pass")
    .reduce((sum, check) => sum + check.weight, 0);
  const tiers = db.sampleIcp.tiers;
  return {
    fitScore: score,
    fitMax: max,
    fitTier: tierFor(max ? Math.round((score / max) * 100) : 0, tiers),
  };
};

export const engagementOf = (
  events: LeadEventDto[],
  scoring: SendingSettingsDto["scoring"],
): EngagementDto => {
  const human = events.filter((item) => !item.machine);
  const count = (kind: LeadEventDto["kind"]) => human.filter((item) => item.kind === kind).length;
  const opens = count("opened");
  const clicks = count("clicked");
  const visits = count("visited");
  const replies = count("replied");
  return {
    score:
      opens * scoring.open +
      clicks * scoring.click +
      visits * (scoring.visit ?? 0) +
      replies * (scoring.reply ?? 0),
    opens,
    clicks,
    visits,
    replies,
    hiddenMachineEvents: events.length - human.length,
  };
};

const lastOf = (events: LeadEventDto[], kind: LeadEventDto["kind"]) =>
  events
    .filter((item) => item.kind === kind && !item.machine)
    .map((item) => item.at)
    .sort()
    .at(-1) ?? null;

const lastBounced = (person: SamplePerson) => {
  const sentAt = lastOf(person.events, "sent");
  const bouncedAt = lastOf(person.events, "bounced");
  return Boolean(bouncedAt && (!sentAt || bouncedAt >= sentAt));
};

/** Which channels we may use for this person, and why. Australian rules (see the proposal). */
export const channelsFor = (
  db: SampleDb,
  person: SamplePerson,
  account: SampleAccount,
): ChannelRuleDto[] => {
  if (account.status === "held" || account.status === "excluded") {
    const why =
      account.status === "held"
        ? `The account is on hold${account.statusNote ? `: ${account.statusNote}` : "."}`
        : "The account is excluded.";
    return [
      { channel: "email", allowed: false, basis: why },
      { channel: "phone", allowed: false, basis: why },
      { channel: "linkedin", allowed: false, basis: why },
    ];
  }
  const suppressed =
    person.email !== null &&
    db.settings.suppression.some((row) => row.email.toLowerCase() === person.email!.toLowerCase());
  const emailRule = (): ChannelRuleDto => {
    if (suppressed)
      return { channel: "email", allowed: false, basis: "On the suppression list. Never email." };
    if (lastBounced(person))
      return {
        channel: "email",
        allowed: false,
        basis: "The last email bounced. Check they're still in the role before trying again.",
      };
    switch (person.contactStatus) {
      case "published":
        return {
          channel: "email",
          allowed: true,
          basis:
            "Published by the account for this role. Consent can be inferred under the Spam Act when the message is relevant to their role; every email names the sender and has an opt-out.",
        };
      case "pattern_verified":
        return {
          channel: "email",
          allowed: false,
          basis:
            "The address exists but was never published, so consent can't be inferred. In Australia we don't cold email it. Use LinkedIn or the team inbox.",
        };
      case "guessed":
        return {
          channel: "email",
          allowed: false,
          basis: "Guessed from the account's pattern; not published or verified.",
        };
      default:
        return { channel: "email", allowed: false, basis: "No address found." };
    }
  };
  const phone = person.phone ?? account.phone;
  return [
    emailRule(),
    phone
      ? {
          channel: "phone",
          allowed: true,
          basis: person.phone
            ? "A published business line. Check the Do Not Call Register before calling a mobile."
            : "The account's published switchboard.",
        }
      : { channel: "phone", allowed: false, basis: "No published business number." },
    person.linkedin.url
      ? {
          channel: "linkedin",
          allowed: true,
          basis: "By hand only, from your own account. Agents never touch LinkedIn.",
        }
      : { channel: "linkedin", allowed: false, basis: "No profile found yet." },
  ];
};

const bestChannelOf = (
  rules: ChannelRuleDto[],
  account: SampleAccount,
): LeadPersonDto["bestChannel"] => {
  const ok = (channel: ChannelRuleDto["channel"]) =>
    rules.find((rule) => rule.channel === channel)?.allowed;
  if (ok("email")) return "email";
  if (ok("linkedin")) return "linkedin";
  if (account.teamInbox && account.status !== "held" && account.status !== "excluded")
    return "team_inbox";
  if (ok("phone")) return "phone";
  return "none";
};

const segmentName = (db: SampleDb, id: string) =>
  db.sampleIcp.segments.find((segment) => segment.id === id)?.name ?? "Unassigned";

const personDto = (db: SampleDb, person: SamplePerson): LeadPersonDto => {
  const account = db.accounts.find((item) => item.id === person.accountId)!;
  const rules = channelsFor(db, person, account);
  return {
    id: person.id,
    accountId: person.accountId,
    accountName: account.name,
    name: person.name,
    title: person.title,
    role: person.role,
    email: person.email,
    contactStatus: person.contactStatus,
    stage: person.stage,
    owner: person.owner,
    engagement: engagementOf(person.events, db.settings.scoring),
    lastEmailAt: lastOf(person.events, "sent"),
    lastVisitAt: lastOf(person.events, "visited"),
    linkedin: person.linkedin,
    bestChannel: bestChannelOf(rules, account),
  };
};

const accountDto = (db: SampleDb, account: SampleAccount): LeadAccountDto => {
  const people = db.people.filter((person) => person.accountId === account.id);
  const fresh = [...account.signals].sort((a, b) => b.seenAt.localeCompare(a.seenAt))[0];
  return {
    id: account.id,
    name: account.name,
    segmentId: account.segmentId,
    segmentName: segmentName(db, account.segmentId),
    website: account.website,
    location: account.location,
    ...fitOf(db, account),
    checkCounts: {
      pass: account.checks.filter((check) => check.verdict === "pass").length,
      unknown: account.checks.filter((check) => check.verdict === "unknown").length,
      fail: account.checks.filter((check) => check.verdict === "fail").length,
    },
    signal: fresh?.text ?? null,
    people: people.length,
    emailable: people.filter((person) =>
      channelsFor(db, person, account).some((rule) => rule.channel === "email" && rule.allowed),
    ).length,
    status: account.status,
    statusNote: account.statusNote,
    source: account.source,
    foundAt: account.foundAt,
    icpVersion: account.icpVersion,
  };
};

const messageDto = (db: SampleDb, message: SampleDb["messages"][number]): OutreachMessageDto => {
  const person = db.people.find((item) => item.id === message.personId)!;
  const account = db.accounts.find((item) => item.id === person.accountId)!;
  return {
    ...message,
    personName: person.name,
    accountName: account.name,
    to: person.email,
    engagement: engagementOf(
      person.events.filter((item) => item.messageId === message.id),
      db.settings.scoring,
    ),
  };
};

// ─── Checks before sending ───────────────────────────────────────────────────────────────────

export type SendCheck = {
  id: string;
  label: string;
  state: "pass" | "warn" | "fail";
  detail: string;
};

const PLACEHOLDER = /\{[^}]*\}|\[[A-Z][^\]]*\]|<[A-Z][A-Za-z ]*>/;

/** The same checks the server will run. Any "fail" blocks sending. */
export const preSendChecks = (
  input: { subject: string; body: string; claimsUsed: string[] },
  person: LeadPersonDetailDto,
  sender: SenderDto | undefined,
  settings: SendingSettingsDto,
): SendCheck[] => {
  const text = `${input.subject}\n${input.body}`;
  const placeholder = text.match(PLACEHOLDER)?.[0];
  const greeting = input.body.split("\n")[0] ?? "";
  const first = firstName(person.name);
  const emailRule = person.channels.find((rule) => rule.channel === "email");
  const suppressed = settings.suppression.some(
    (row) => person.email && row.email.toLowerCase() === person.email.toLowerCase(),
  );
  const domainOk =
    sender &&
    sender.domain.spf === "pass" &&
    sender.domain.dkim === "pass" &&
    sender.domain.dmarc === "pass";
  const roleAge = Math.floor(days(person.found.seenAt));
  return [
    {
      id: "placeholder",
      label: "No unfilled placeholders",
      state: placeholder ? "fail" : "pass",
      detail: placeholder ? `Found ${placeholder}` : "Every merge field is filled in.",
    },
    {
      id: "name",
      label: "Greets them by name",
      state: greeting.includes(first) ? "pass" : "warn",
      detail: greeting.includes(first)
        ? `Opens with “${greeting.trim().slice(0, 40)}”.`
        : `The first line doesn't say “${first}”.`,
    },
    {
      id: "basis",
      label: "Allowed to email this address",
      state: emailRule?.allowed ? "pass" : "fail",
      detail: emailRule?.basis ?? "No email rule.",
    },
    {
      id: "suppression",
      label: "Not on the suppression list",
      state: suppressed ? "fail" : "pass",
      detail: suppressed ? "They asked not to be contacted." : "Checked just now.",
    },
    {
      id: "role",
      label: "Role checked recently",
      state: roleAge <= 30 ? "pass" : "warn",
      detail:
        roleAge <= 30
          ? `Seen in the role ${roleAge === 0 ? "today" : `${roleAge} days ago`}.`
          : `Last seen in the role ${roleAge} days ago. Re-check before sending.`,
    },
    {
      id: "claims",
      label: "Claims are approved",
      state: "pass",
      detail: input.claimsUsed.length
        ? `Relies on ${input.claimsUsed.join(", ")}.`
        : "States no product facts.",
    },
    {
      id: "optout",
      label: "Signature and opt-out line",
      state: sender?.optOutLine.trim() ? "pass" : "fail",
      detail: sender?.optOutLine.trim()
        ? "Added under the signature."
        : "This sender has no opt-out line. Add one in Sending.",
    },
    {
      id: "domain",
      label: "Sending domain is authenticated",
      state: domainOk ? "pass" : "fail",
      detail: sender
        ? `${sender.domain.name}: SPF ${sender.domain.spf}, DKIM ${sender.domain.dkim}, DMARC ${sender.domain.dmarc}.`
        : "No sender.",
    },
    {
      id: "cap",
      label: "Under today's limit",
      state: sender && sender.sentToday < sender.dailyCap ? "pass" : "fail",
      detail: sender ? `${sender.sentToday} of ${sender.dailyCap} sent today.` : "No sender.",
    },
    {
      id: "provider",
      label: "Sending provider",
      state: settings.provider.connected ? "pass" : "warn",
      detail: settings.provider.connected
        ? "Connected."
        : "Not connected yet, so sending runs in test mode: it's recorded here and nothing leaves the platform.",
    },
  ];
};

// ─── Reads ───────────────────────────────────────────────────────────────────────────────────

export type SampleRef = { brandId: string; brandName: string };

/**
 * Results are copied, as they would be over the network. Handing out the store's own objects
 * let later writes change cached data in place, so React Query saw "no change" and the screen
 * kept showing the old version.
 */
const copy = <T>(value: T): T => (value === undefined ? value : structuredClone(value));

const read = <T>(ref: SampleRef, fn: (db: SampleDb) => T) =>
  later(() => copy(fn(load(ref.brandId, ref.brandName))));

const write = <T>(ref: SampleRef, fn: (db: SampleDb) => T) =>
  later(() => {
    const db = load(ref.brandId, ref.brandName);
    const result = fn(db);
    save(ref.brandId, db);
    return copy(result);
  }, 350);

export const sample = {
  overview: (ref: SampleRef) =>
    read(ref, (db): LeadsOverviewDto => {
      const people = db.people.map((person) => personDto(db, person));
      const accounts = db.accounts.map((account) => accountDto(db, account));
      const toWrite: ToWriteDto[] = [];
      for (const person of db.people) {
        const dto = personDto(db, person);
        const human = person.events.filter((item) => !item.machine);
        const reply = human.filter((item) => item.kind === "replied").at(-1);
        const lastSent = lastOf(person.events, "sent");
        const draft = db.messages.some(
          (message) => message.personId === person.id && message.status === "draft",
        );
        if (draft) continue;
        if (reply && (!lastSent || lastSent < reply.at)) {
          toWrite.push({
            person: dto,
            urgency: "reply",
            reasons: [
              `Replied ${Math.max(1, Math.round(days(reply.at)))} days ago: ${reply.detail}`,
              "Nothing has gone back yet",
            ],
          });
          continue;
        }
        const recentVisit = human.find((item) => item.kind === "visited" && days(item.at) <= 7);
        const recentClick = human.find((item) => item.kind === "clicked" && days(item.at) <= 7);
        if (recentVisit || recentClick) {
          toWrite.push({
            person: dto,
            urgency: "hot",
            reasons: [
              recentVisit ? `${recentVisit.detail}` : "Clicked a link in your email",
              `Score ${dto.engagement.score}`,
            ],
          });
          continue;
        }
        const accepted = person.linkedin.acceptedAt;
        if (accepted && days(accepted) <= 14 && !lastSent) {
          toWrite.push({
            person: dto,
            urgency: "linkedin",
            reasons: [
              `Accepted your LinkedIn request ${Math.max(1, Math.round(days(accepted)))} days ago`,
              "Send a short note on LinkedIn",
            ],
          });
          continue;
        }
        if (
          lastSent &&
          dto.stage === "contacted" &&
          days(lastSent) >= db.settings.followUpAfterDays &&
          !lastBounced(person)
        ) {
          toWrite.push({
            person: dto,
            urgency: "follow_up",
            reasons: [
              `Emailed ${Math.round(days(lastSent))} days ago${dto.engagement.opens ? ", opened, no reply" : ", no reply"}`,
              "A follow-up is due",
            ],
          });
        }
      }
      const order = { reply: 0, hot: 1, linkedin: 2, follow_up: 3 } as const;
      toWrite.sort(
        (a, b) =>
          order[a.urgency] - order[b.urgency] ||
          b.person.engagement.score - a.person.engagement.score,
      );
      const recent = db.people
        .flatMap((person) =>
          person.events
            .filter((item) => !item.machine && item.kind !== "delivered")
            .map((item) => ({ ...item, personId: person.id, personName: person.name })),
        )
        .sort((a, b) => b.at.localeCompare(a.at))
        .slice(0, 8);
      return {
        accounts: {
          total: accounts.filter((account) => account.status !== "excluded").length,
          tierA: accounts.filter(
            (account) => account.fitTier === "A" && account.status !== "excluded",
          ).length,
          toReview: accounts.filter((account) => account.status === "suggested").length,
        },
        people: {
          total: people.length,
          emailable: people.filter((person) => person.bestChannel === "email").length,
          linkedinOnly: people.filter((person) => person.bestChannel === "linkedin").length,
        },
        outreach: {
          drafts: db.messages.filter((message) => message.status === "draft").length,
          sent: db.messages.filter((message) => message.status === "sent").length,
          replied: people.filter((person) => person.engagement.replies > 0).length,
        },
        toWrite,
        recent,
      };
    }),

  accounts: (ref: SampleRef) =>
    read(ref, (db) =>
      db.accounts
        .map((account) => accountDto(db, account))
        .sort(
          (a, b) =>
            a.fitTier.localeCompare(b.fitTier) || b.fitScore / b.fitMax - a.fitScore / a.fitMax,
        ),
    ),

  account: (ref: SampleRef, id: string) =>
    read(ref, (db): LeadAccountDetailDto => {
      const account = db.accounts.find((item) => item.id === id);
      if (!account) throw new Error("That account isn't in this brand's list.");
      return {
        ...accountDto(db, account),
        checks: account.checks,
        signals: account.signals,
        teamInbox: account.teamInbox,
        phone: account.phone,
        emailPattern: account.emailPattern,
        people: db.people
          .filter((person) => person.accountId === id)
          .map((person) => personDto(db, person)),
        researchCostUsd: account.researchCostUsd,
      };
    }),

  people: (ref: SampleRef) =>
    read(ref, (db) =>
      db.people
        .map((person) => personDto(db, person))
        .sort((a, b) => b.engagement.score - a.engagement.score || a.name.localeCompare(b.name)),
    ),

  person: (ref: SampleRef, id: string) =>
    read(ref, (db): LeadPersonDetailDto => {
      const person = db.people.find((item) => item.id === id);
      if (!person) throw new Error("That person isn't in this brand's list.");
      const account = db.accounts.find((item) => item.id === person.accountId)!;
      return {
        ...personDto(db, person),
        phone: person.phone,
        found: person.found,
        channels: channelsFor(db, person, account),
        leadSource: `Growth Engine – ${db.brandLabel}`,
        events: [...person.events].reverse().sort((a, b) => b.at.localeCompare(a.at)),
        notes: person.notes,
      };
    }),

  messages: (ref: SampleRef) =>
    read(ref, (db) =>
      db.messages
        .map((message) => messageDto(db, message))
        .sort((a, b) => (b.sentAt ?? b.createdAt).localeCompare(a.sentAt ?? a.createdAt)),
    ),

  message: (ref: SampleRef, id: string) =>
    read(ref, (db) => {
      const message = db.messages.find((item) => item.id === id);
      if (!message) throw new Error("That email isn't here any more.");
      return messageDto(db, message);
    }),

  settings: (ref: SampleRef) => read(ref, (db) => db.settings),

  // ─── Writes ──────────────────────────────────────────────────────────────────────────────────

  setAccountStatus: (ref: SampleRef, id: string, status: SampleAccount["status"], note: string) =>
    write(ref, (db) => {
      const account = db.accounts.find((item) => item.id === id);
      if (!account) throw new Error("That account isn't in this brand's list.");
      account.status = status;
      account.statusNote = note;
    }),

  findAccounts: (ref: SampleRef) =>
    write(ref, (db) => {
      const next = db.moreAccounts.splice(0, 2);
      if (!next.length)
        throw new Error("No more sample accounts. Real searches come with the next phase.");
      db.accounts.push(...next);
      return next.length;
    }),

  setStage: (ref: SampleRef, id: string, stage: LeadStage) =>
    write(ref, (db) => {
      const person = db.people.find((item) => item.id === id);
      if (person) person.stage = stage;
    }),

  setLinkedIn: (ref: SampleRef, id: string, field: "sent" | "accepted", on: boolean, by: string) =>
    write(ref, (db) => {
      const person = db.people.find((item) => item.id === id);
      if (!person) return;
      const when = on ? now() : null;
      if (field === "sent") person.linkedin.requestSentAt = when;
      else person.linkedin.acceptedAt = when;
      if (on) {
        person.events.push({
          id: newId("ev"),
          at: now(),
          kind: field === "sent" ? "linkedin_sent" : "linkedin_accepted",
          messageId: null,
          detail: field === "sent" ? `Connection request sent by ${by}` : "Accepted",
          machine: false,
          machineReason: null,
        });
        if (person.stage === "new") person.stage = "contacted";
      }
    }),

  addNote: (ref: SampleRef, id: string, text: string) =>
    write(ref, (db) => {
      const person = db.people.find((item) => item.id === id);
      if (!person) return;
      person.notes = person.notes ? `${person.notes}\n${text}` : text;
      person.events.push({
        id: newId("ev"),
        at: now(),
        kind: "note",
        messageId: null,
        detail: text,
        machine: false,
        machineReason: null,
      });
    }),

  logReply: (ref: SampleRef, id: string, text: string) =>
    write(ref, (db) => {
      const person = db.people.find((item) => item.id === id);
      if (!person) return;
      const lastMessage = db.messages
        .filter((message) => message.personId === id && message.status === "sent")
        .sort((a, b) => (b.sentAt ?? "").localeCompare(a.sentAt ?? ""))[0];
      person.events.push({
        id: newId("ev"),
        at: now(),
        kind: "replied",
        messageId: lastMessage?.id ?? null,
        detail: `“${text}”`,
        machine: false,
        machineReason: null,
      });
      person.stage = "replied";
    }),

  createDraft: (ref: SampleRef, personId: string) =>
    write(ref, (db) => {
      const person = db.people.find((item) => item.id === personId);
      if (!person) throw new Error("That person isn't in this brand's list.");
      const existing = db.messages.find(
        (message) => message.personId === personId && message.status === "draft",
      );
      if (existing) return existing.id;
      const account = db.accounts.find((item) => item.id === person.accountId)!;
      const followUp = person.events.some((item) => item.kind === "sent");
      const name = firstName(person.name);
      const sender = db.settings.senders[0];
      const id = newId("m");
      db.messages.push({
        id,
        personId,
        senderId: sender?.id ?? "",
        kind: followUp ? "follow_up" : "first",
        subject: followUp
          ? `Re: interview practice at ${account.name}`
          : `Interview practice at ${account.name}`,
        body: followUp
          ? `Hi ${name},\n\nA quick follow-up in case my last note got buried. If students at ${account.name} would benefit from practising interviews out loud before they see your team, you can try a live interview yourself in about two minutes: firstround.com.au\n\nHappy to answer anything by email if that's easier.`
          : `Hi ${name},\n\nI saw that ${account.name} helps students prepare for interviews. Most students who need the practice never get a mock interview, simply because there aren't enough appointments.\n\nFirst Round lets students practise a job interview out loud with an AI interviewer that follows up when an answer is vague. Feedback is ready when they hang up.\n\nYou can try it yourself in about two minutes, no card needed: firstround.com.au\n\nWorth a 20-minute look?`,
        status: "draft",
        claimsUsed: followUp ? ["A1", "A6"] : ["A1", "A2", "A14", "A6"],
        createdAt: now(),
        sentAt: null,
        sentBy: null,
        testMode: false,
        sendBackNote: null,
      });
      return id;
    }),

  updateMessage: (ref: SampleRef, id: string, input: UpdateMessageDto & { senderId?: string }) =>
    write(ref, (db) => {
      const message = db.messages.find((item) => item.id === id);
      if (!message) throw new Error("That email isn't here any more.");
      if (message.status === "sent" || message.status === "bounced")
        throw new Error("Sent emails can't be edited.");
      message.subject = input.subject;
      message.body = input.body;
      if (input.senderId) message.senderId = input.senderId;
      if (message.status === "sent_back") message.status = "draft";
    }),

  sendBack: (ref: SampleRef, id: string, note: string) =>
    write(ref, (db) => {
      const message = db.messages.find((item) => item.id === id);
      if (!message || message.status !== "draft") throw new Error("Only drafts can be sent back.");
      message.status = "sent_back";
      message.sendBackNote = note;
    }),

  deleteDraft: (ref: SampleRef, id: string) =>
    write(ref, (db) => {
      db.messages = db.messages.filter(
        (message) => !(message.id === id && message.status !== "sent"),
      );
    }),

  /** Approve and send. Without a provider it records the send in test mode. */
  send: (ref: SampleRef, id: string, by: string) =>
    write(ref, (db) => {
      const message = db.messages.find((item) => item.id === id);
      if (!message || message.status !== "draft") throw new Error("Only drafts can be sent.");
      const person = db.people.find((item) => item.id === message.personId)!;
      const account = db.accounts.find((item) => item.id === person.accountId)!;
      const sender = db.settings.senders.find((item) => item.id === message.senderId);
      const detail: LeadPersonDetailDto = {
        ...personDto(db, person),
        phone: person.phone,
        found: person.found,
        channels: channelsFor(db, person, account),
        leadSource: "",
        events: person.events,
        notes: person.notes,
      };
      const failed = preSendChecks(message, detail, sender, db.settings).filter(
        (item) => item.state === "fail",
      );
      if (failed.length)
        throw new Error(`Can't send yet: ${failed.map((item) => item.label).join(", ")}.`);
      const testMode = !db.settings.provider.connected;
      message.status = "sent";
      message.sentAt = now();
      message.sentBy = by;
      message.testMode = testMode;
      sender!.sentToday += 1;
      person.events.push({
        id: newId("ev"),
        at: now(),
        kind: "sent",
        messageId: message.id,
        detail: testMode
          ? `${message.kind === "first" ? "First email" : "Follow-up"} recorded in test mode: “${message.subject}” (not delivered)`
          : `${message.kind === "first" ? "First email" : "Follow-up"}: “${message.subject}”`,
        machine: false,
        machineReason: null,
      });
      if (person.stage === "new") person.stage = "contacted";
    }),

  saveSettings: (ref: SampleRef, patch: Partial<SendingSettingsDto>) =>
    write(ref, (db) => {
      db.settings = { ...db.settings, ...patch };
      return db.settings;
    }),

  saveSender: (ref: SampleRef, sender: SenderDto) =>
    write(ref, (db) => {
      const exists = db.settings.senders.some((item) => item.id === sender.id);
      db.settings.senders = exists
        ? db.settings.senders.map((item) => (item.id === sender.id ? sender : item))
        : [...db.settings.senders, { ...sender, id: newId("s") }];
      return db.settings;
    }),

  suppress: (ref: SampleRef, email: string, reason: string) =>
    write(ref, (db) => {
      const clean = email.trim().toLowerCase();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean))
        throw new Error("That isn't an email address.");
      if (!db.settings.suppression.some((row) => row.email === clean))
        db.settings.suppression = [
          { email: clean, reason: reason || "Added by hand", at: now() },
          ...db.settings.suppression,
        ];
      return db.settings;
    }),

  unsuppress: (ref: SampleRef, email: string) =>
    write(ref, (db) => {
      db.settings.suppression = db.settings.suppression.filter((row) => row.email !== email);
      return db.settings;
    }),
};
